import React from "react";
import styled from "styled-components";
import { Card, Empty, Progress, Space, Tag, Typography } from "antd";

const { Text } = Typography;

const WidgetCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  height: 100%;

  .ant-card-head-title {
    color: #005249;
    font-weight: 700;
  }
`;

const CategoryList = styled.div`
  display: grid;
  gap: 12px;
`;

const CategoryRow = styled.a`
  display: block;
  border: 1px solid ${({ $severity }) => ($severity === "CRITICAL" ? "#ffccc7" : "#f0f0f0")};
  border-radius: 8px;
  padding: 12px;
  background: ${({ $severity }) => ($severity === "CRITICAL" ? "#fff2f0" : "#ffffff")};
  color: inherit;

  &:hover {
    color: inherit;
    border-color: #005249;
  }
`;

const CategoryHeader = styled(Space)`
  width: 100%;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const DelayIncidentAnalysisWidget = ({ analysis }) => {
  if (!analysis?.categories?.length) {
    return (
      <WidgetCard title="Delays and Incidents">
        <Empty
          description="No current operational issues for the selected filters"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </WidgetCard>
    );
  }

  const maxCount = Math.max(...analysis.categories.map((category) => category.count), 1);

  return (
    <WidgetCard
      title="Delays and Incidents"
      extra={<Tag color="red">{formatNumber(analysis.summary?.totalIssues)} issues</Tag>}
      data-testid="delay-incident-analysis-widget"
    >
      <CategoryList>
        {analysis.categories.map((category) => (
          <CategoryRow
            key={category.key}
            href={category.deepLink || undefined}
            $severity={category.severity}
            data-drilldown-key={category.drilldownKey || undefined}
          >
            <CategoryHeader>
              <Space size={6} wrap>
                <Text strong>{category.label}</Text>
                <Tag color={typeColor(category.type)}>{category.type}</Tag>
                <Tag color={severityColor(category.severity)}>{category.severity}</Tag>
              </Space>
              <Text>{formatNumber(category.count)}</Text>
            </CategoryHeader>
            <Progress
              percent={Math.round((category.count / maxCount) * 100)}
              showInfo={false}
              strokeColor={category.severity === "CRITICAL" ? "#ff4d4f" : "#005249"}
            />
            <Space direction="vertical" size={2}>
              <Text type="secondary">{category.explanation}</Text>
              <Text>{category.suggestedAction}</Text>
            </Space>
          </CategoryRow>
        ))}
      </CategoryList>
    </WidgetCard>
  );
};

function typeColor(type) {
  if (type === "incident") return "red";
  if (type === "exception") return "orange";
  if (type === "trust") return "purple";
  return "gold";
}

function severityColor(severity) {
  if (severity === "CRITICAL" || severity === "HIGH") return "red";
  if (severity === "MEDIUM") return "orange";
  return "default";
}

function formatNumber(value) {
  return new Intl.NumberFormat("en-ZA").format(Number(value || 0));
}

export default DelayIncidentAnalysisWidget;
